import { createFileRoute, redirect } from '@tanstack/react-router'
import { Suspense, useCallback, useEffect } from 'react'
import { Layout } from '@/shared/components/Layout'
import { useAuthStore } from '@/shared/store/authStore'
import { useRedactorStore } from '@/features/message-redactor/store/redactorStore'
import { useHistory } from '@/features/message-redactor/hooks/useHistory'
import { RedactorForm } from '@/features/message-redactor/components/RedactorForm'
import { DiffView } from '@/features/message-redactor/components/DiffView'
import { RedactorHistory } from '@/features/message-redactor/components/RedactorHistory'
import { SkeletonRedactor } from '@/features/message-redactor/components/SkeletonRedactor'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { Button } from '@/shared/components/ui/button'
import { Separator } from '@/shared/components/ui/separator'
import { Badge } from '@/shared/components/ui/badge'
import { BookMarked, ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'

function MessageRedactorPage() {
  const { inputText, outputText, status, tone, audience, messageType, skillId, inputTokens, outputTokens } =
    useRedactorStore()
  const { save } = useHistory()
  const [historyOpen, setHistoryOpen] = useState(false)
  const [saved, setSaved] = useState(false)

  const handleSave = useCallback(async () => {
    if (!outputText || !inputText) return
    await save({
      originalText: inputText,
      redactedText: outputText,
      tone,
      audience,
      messageType,
      skillId,
      inputTokens: inputTokens ?? null,
      outputTokens: outputTokens ?? null,
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }, [inputText, outputText, tone, audience, messageType, skillId, inputTokens, outputTokens, save])

  // Cmd+S to save
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's' && status === 'done') {
        e.preventDefault()
        void handleSave()
      }
    },
    [handleSave, status]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  return (
    <Layout>
      <div className="mx-auto max-w-5xl p-6 space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Message Redactor</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Polish your message with AI — tone, audience, and format controls
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHistoryOpen((o) => !o)}
            className="gap-2"
          >
            <BookMarked className="h-4 w-4" />
            History
            {historyOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </Button>
        </div>

        {historyOpen && (
          <div className="rounded-lg border bg-card p-4">
            <h2 className="text-sm font-semibold mb-3">Recent redactions</h2>
            <ErrorBoundary>
              <RedactorHistory />
            </ErrorBoundary>
          </div>
        )}

        <ErrorBoundary>
          <Suspense fallback={<SkeletonRedactor />}>
            <RedactorForm />
          </Suspense>
        </ErrorBoundary>

        {(status === 'streaming' || status === 'done' || status === 'aborted') && outputText && (
          <>
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold">Result</h2>
                <div className="flex items-center gap-2">
                  {inputTokens != null && outputTokens != null && (
                    <Badge variant="outline" className="text-xs font-mono">
                      {inputTokens + outputTokens} tokens
                    </Badge>
                  )}
                  {status === 'done' && (
                    <Button size="sm" variant="outline" onClick={() => void handleSave()} disabled={saved}>
                      {saved ? 'Saved!' : 'Save to history'}
                      <span className="ml-1 text-xs text-muted-foreground">⌘S</span>
                    </Button>
                  )}
                </div>
              </div>

              {status === 'streaming' && (
                <div className="rounded-md border bg-muted/30 p-3 text-sm font-mono whitespace-pre-wrap">
                  {outputText}
                  <span className="animate-pulse">▍</span>
                </div>
              )}

              {(status === 'done' || status === 'aborted') && inputText && outputText && (
                <DiffView original={inputText} redacted={outputText} />
              )}
            </div>
          </>
        )}

        {status === 'error' && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            Something went wrong. Please try again.
          </div>
        )}
      </div>
    </Layout>
  )
}

export const Route = createFileRoute('/dashboard/message-redactor')({
  beforeLoad: () => {
    const { session, loading } = useAuthStore.getState()
    if (!loading && !session) {
      throw redirect({ to: '/login' })
    }
  },
  component: MessageRedactorPage,
})
